import React, { useState, useEffect, useCallback, memo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FormField = memo(({ field, value, onChange, token, language }) => {
  const handleChange = useCallback((e) => {
    const val = field.type === 'number' ? parseFloat(e.target.value) : e.target.value;
    onChange(field.key, val);
  }, [field.key, field.type, onChange]);

  const handleSwitch = useCallback((checked) => {
    onChange(field.key, checked);
  }, [field.key, onChange]);

  const handleSelect = useCallback((v) => {
    onChange(field.key, v);
  }, [field.key, onChange]);

  if (field.type === 'textarea') {
    return (
      <div>
        <Label htmlFor={field.key}>{field.label}</Label>
        <Textarea
          id={field.key}
          value={value || ''}
          onChange={handleChange}
          className="mt-1"
          required={field.required}
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === 'switch') {
    return (
      <div>
        <Label htmlFor={field.key}>{field.label}</Label>
        <div className="mt-1">
          <Switch
            checked={value ?? true}
            onCheckedChange={handleSwitch}
            data-testid={`field-${field.key}`}
          />
        </div>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        <Label htmlFor={field.key}>{field.label}</Label>
        <Select value={value || field.options[0]} onValueChange={handleSelect}>
          <SelectTrigger className="mt-1" data-testid={`field-${field.key}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options.map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'file') {
    return (
      <div>
        <Label htmlFor={field.key}>{field.label}</Label>
        <div className="mt-1 space-y-2">
          {value && (
            <div className="relative w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
              <img src={value} alt="Preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onChange(field.key, '')}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center"
              >
                <X size={10} />
              </button>
            </div>
          )}
          <Input
            id={field.key}
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                const formData = new FormData();
                formData.append('file', file);
                try {
                  const res = await fetch(`${API_URL}/api/upload`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData
                  });
                  if (res.ok) {
                    const data = await res.json();
                    onChange(field.key, `${API_URL}${data.url}`);
                    toast.success(language === 'pt' ? 'Imagem carregada!' : 'Image uploaded!');
                  }
                } catch {
                  toast.error(language === 'pt' ? 'Erro ao carregar' : 'Upload failed');
                }
              }
            }}
            className="mt-1"
            data-testid={`field-${field.key}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor={field.key}>{field.label}</Label>
      <Input
        id={field.key}
        type={field.type}
        step={field.step}
        value={value ?? ''}
        onChange={handleChange}
        className="mt-1"
        placeholder={field.placeholder}
        required={field.required}
        data-testid={`field-${field.key}`}
      />
    </div>
  );
});

FormField.displayName = 'FormField';

const EditFormDialog = ({ open, type, data, fields, onClose, onSave, language, token }) => {
  const [formData, setFormData] = useState({});
  const isEdit = !!data;

  const fieldsRef = React.useRef(fields);
  fieldsRef.current = fields;

  // Only re-initialize when the dialog opens or targets a different record —
  // NOT on parent re-renders (polling/WS), which would wipe unsaved input.
  useEffect(() => {
    if (!open) return;
    if (data) {
      setFormData({ ...data });
    } else {
      const defaults = {};
      fieldsRef.current.forEach(f => {
        if (f.type === 'switch') defaults[f.key] = true;
        else if (f.type === 'number') defaults[f.key] = 0;
        else defaults[f.key] = '';
      });
      setFormData(defaults);
    }
  }, [open, data]);

  const handleFieldChange = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, isEdit);
  };

  const typeLabels = {
    restaurant: language === 'pt' ? 'Restaurante' : 'Restaurant',
    menuItem: language === 'pt' ? 'Item do Menu' : 'Menu Item',
    driver: language === 'pt' ? 'Entregador' : 'Driver',
    shisha: language === 'pt' ? 'Modelo de Shisha' : 'Shisha Model',
    flavor: language === 'pt' ? 'Sabor' : 'Flavor',
    shisha_extra: language === 'pt' ? 'Extra da Shisha' : 'Shisha Extra',
    zone: language === 'pt' ? 'Zona de Entrega' : 'Delivery Zone',
    category: language === 'pt' ? 'Categoria' : 'Category',
    ice: language === 'pt' ? 'Produto de Gelo' : 'Ice Product',
  };
  const friendlyType = typeLabels[type] || type;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? (language === 'pt' ? 'Editar' : 'Edit') : (language === 'pt' ? 'Adicionar' : 'Add')} {friendlyType}
          </DialogTitle>
          <DialogDescription>
            {language === 'pt' ? 'Preencha os campos abaixo e clique em Salvar.' : 'Fill in the fields below and click Save.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(field => (
            <FormField
              key={field.key}
              field={field}
              value={formData[field.key]}
              onChange={handleFieldChange}
              token={token}
              language={language}
            />
          ))}
          <div className="flex gap-2 pt-4">
            <Button type="submit" className="flex-1 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" data-testid="save-form-btn">
              <Save size={18} className="mr-2" />
              {language === 'pt' ? 'Salvar' : 'Save'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">
              {language === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditFormDialog;
